import React, { MouseEvent, Ref, forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { FieldContained } from '@ad-scripts/presentation/components/common/input_field';
import { errorCss } from '@ad-scripts/presentation/components/common/textarea_field';
import { ErrorLabel } from '@ad-scripts/presentation/components/common/forms/control_container';
import {
  ModalContainer,
  StyledAddModelContainer,
  StyledEmbedModalWithGreenHeader,
  StyledFooter,
} from 'src/presentation/components/pages/sidekick/threads/components/Layout';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import { Row } from 'src/presentation/components/forms/Layout';
import Textarea from 'src/presentation/components/forms/Textarea';

type ExpertModeModalProps = {};

type ShowModalProps = {
  subscribe: (params: unknown) => void;
  title: string;
  json: string;
};

type OnSelectCallback = (json: string) => void;

export type ExpertModeModalRef = {
  show: ({ title, json, subscribe }: ShowModalProps) => void;
};

const ExpertModeModal = forwardRef((_: ExpertModeModalProps, ref: Ref<ExpertModeModalRef>) => {
  const refCallbacks = useRef<{ subscribe: (params: unknown) => void } | null>(null);
  const [title, setTitle] = useState<string>('');
  const [json, setJson] = useState<string>('');

  const [modalVisible, setModalVisible] = useState(false);

  useImperativeHandle(ref, () => ({
    show({ title, json, subscribe }: ShowModalProps) {
      refCallbacks.current = { subscribe };
      setModalVisible(true);
      setTitle(title);
      setJson(json);
    },
  }));

  const handleSelect = (json: string) => {
    if (refCallbacks.current?.subscribe) {
      refCallbacks.current.subscribe(json);
      handleClose();
    }
  };

  const handleClose = () => {
    setModalVisible(false);
    refCallbacks.current = null;
  };

  if (!modalVisible) {
    return null;
  }

  return (
    <StyledEmbedModalWithGreenHeader
      title={title}
      content={<ExpertModeModalContent onSelect={handleSelect} onCancel={handleClose} json={json} />}
      disableOutsideClick={true}
      modalSize={ModalSize.LARGE}
      onCancel={handleClose}
    />
  );
});

const ExpertModeModalContent = ({
  json,
  onSelect,
  onCancel,
}: {
  json: string;
  onSelect: OnSelectCallback;
  onCancel: () => void;
}) => {
  const [value, setValue] = useState<string>(json);
  const [error, setError] = useState<string | null>(null);

  const handleClick = (evn: MouseEvent<HTMLElement>) => {
    evn.preventDefault();
    onSelect(value);
  };

  const isValid = (json: string) => {
    try {
      JSON.parse(json);
      return true;
    } catch (e) {
      return false;
    }
  };

  const isDirty = useMemo(() => {
    try {
      const val = JSON.stringify(JSON.parse(value), null, 2);
      return json !== val;
    } catch (e) {
      return true;
    }
  }, [json, value]);

  const handleChange = evt => {
    setError(null);
    const val = evt.target.value;
    if (!isValid(val)) {
      setError('Invalid JSON');
    }

    setValue(val);
  };

  return (
    <StyledAddModelContainer>
      <ModalContainer>
        <Row>
          <StyledFieldContained
            className="json-text"
            error={error}
            control={<StyledTextarea attr="" isError={!!error} value={value} onChange={handleChange} maxRows={24} />}
          />
        </Row>
      </ModalContainer>
      <StyledFooter>
        <Button onClick={onCancel} type={ButtonTypes.TEXT}>
          Cancel
        </Button>
        <Button onClick={handleClick} disabled={!!error || !isDirty}>
          Save
        </Button>
      </StyledFooter>
    </StyledAddModelContainer>
  );
};

const StyledFieldContained = styled(FieldContained)`
  ${ErrorLabel} {
    padding-top: 8px;
    text-align: left;
  }
`;

const StyledTextarea = styled(Textarea)`
  ${p => p.isError && errorCss};
`;

export default ExpertModeModal;
